import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { Chip, Tooltip, Typography, makeStyles } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import type { SpecIntelligenceFinding } from 'api/graphQL/types/specIntelligence.types'
import clsx from 'clsx'
import React from 'react'
import { CATEGORY_COLORS, CATEGORY_DISPLAY_LABELS, SPEC_INT_AI_COLOR, SPEC_INT_MUTED_TEXT_COLOR, SPEC_INT_NEUTRAL_GRAY_COLOR } from '../../specIntelligenceConstants'
import StateBadge from './StateBadge'

/** Explains the AI badge shown on findings produced by AI-powered analysis. */
const AI_BADGE_TOOLTIP = 'This finding was identified by AI-powered analysis.'

const DEFAULT_CATEGORY_STYLE = { backgroundColor: '#E0E0E0', color: '#424242' }

const useStyles = makeStyles(() => ({
    headerRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: pxToRem(8),
        marginBottom: pxToRem(4),
    },
    sectionInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(4),
        minWidth: 0,
        flexShrink: 1,
        maxWidth: '65%',
    },
    sectionNumber: {
        fontSize: pxToRem(11),
        fontWeight: 600,
        color: '#424242',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    sectionDot: { fontSize: pxToRem(11), color: SPEC_INT_NEUTRAL_GRAY_COLOR, flexShrink: 0 },
    sectionName: {
        fontSize: pxToRem(11),
        color: SPEC_INT_MUTED_TEXT_COLOR,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    headerRight: {
        display: 'flex',
        alignItems: 'center',
        gap: pxToRem(4),
        flexShrink: 0,
    },
    // In Reports view, findings get State badges (Dismissed/Resolved) containing a lengthy date+author stamp; these should grow to fill the space and avoid awkward wrapping.
    // (In the Workspace drawer view, by constrast, the State badges don't appear and the Category badges should not grow, lest they overrun the Expand arrow to their right.)
    headerRightGrow: {
        flex: '1 1 auto',
        minWidth: 0,
    },
    expandIconSpacer: {
        marginLeft: 'auto',
        flexShrink: 0,
    },
    categoryChip: {
        height: pxToRem(20),
        fontSize: pxToRem(10),
        '& .MuiChip-label': { padding: `0 ${pxToRem(6)}` },
    },
    aiBadge: {
        display: 'flex',
        alignItems: 'center',
        flexShrink: 0,
        height: pxToRem(18),
        borderRadius: pxToRem(4),
        padding: `0 ${pxToRem(6)}`,
        backgroundColor: '#EDE7F6',
        color: SPEC_INT_AI_COLOR,
        fontSize: pxToRem(10),
        fontWeight: 700,
        cursor: 'default',
    },
    expandIcon: {
        fontSize: pxToRem(18),
        color: '#757575',
        transition: 'transform 200ms',
    },
    expandIconOpen: { transform: 'rotate(180deg)' },
}))

export interface FindingCardHeaderProps {
    finding: SpecIntelligenceFinding
    expanded: boolean
    readOnly?: boolean
}

/** Header row for a finding card: section number/name, category chip, AI badge, and either an expand arrow or a state badge. */
const FindingCardHeader = ({ finding, expanded, readOnly }: FindingCardHeaderProps) => {
    const classes = useStyles()
    const displayCategory = CATEGORY_DISPLAY_LABELS[finding.category] ?? finding.category
    const chipStyle = CATEGORY_COLORS[finding.category] ?? DEFAULT_CATEGORY_STYLE
    return (
        <div className={classes.headerRow}>
            <div className={classes.sectionInfo}>
                <Typography component="span" className={classes.sectionNumber}>
                    {finding.sectionNumber}
                </Typography>
                <Typography component="span" className={classes.sectionDot}>·</Typography>
                <Tooltip title={finding.sectionName}>
                    <Typography component="span" className={classes.sectionName}>
                        {finding.sectionName}
                    </Typography>
                </Tooltip>
            </div>
            <div
                data-testid="header-right"
                data-grow={readOnly || undefined}
                className={clsx(classes.headerRight, readOnly && classes.headerRightGrow)}
            >
                <Chip
                    label={displayCategory}
                    size="small"
                    className={classes.categoryChip}
                    style={chipStyle}
                />
                {finding.isAI && (
                    <Tooltip title={AI_BADGE_TOOLTIP}>
                        <span className={classes.aiBadge}>AI</span>
                    </Tooltip>
                )}
                {readOnly && (
                    <StateBadge
                        state={finding.resolutionState}
                        resolvedByName={finding.resolvedByName}
                        resolvedAt={finding.resolvedAt}
                    />
                )}
            </div>
            {!readOnly && (
                <ExpandMoreIcon
                    className={clsx(classes.expandIcon, classes.expandIconSpacer, expanded && classes.expandIconOpen)}
                    aria-hidden="true"
                />
            )}
        </div>
    )
}

export default FindingCardHeader
